import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Search, ClipboardList } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  declined: "bg-red-100 text-red-800 border-red-200",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—");

const MyReferrals = () => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (name) params.append("name", name);
      if (dob) params.append("dob", dob);
      const res = await axios.get(`${API}/referrals/mine?${params.toString()}`);
      setItems(res.data.items || []);
    } catch {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const lastReferral = items.length > 0 ? items[0] : null;

  return (
    <div className="space-y-6" data-testid="my-referrals-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>My Referrals</h1>
        <p className="text-slate-500 mt-1">Search your submitted referrals by name and date of birth</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Search className="w-5 h-5 text-amber-600" />Cross-Reference a Client</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div><Label>Name</Label><Input className="mt-1" data-testid="search-name" placeholder="First or last name" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div><Label>Date of Birth</Label><Input type="date" className="mt-1" data-testid="search-dob" value={dob} onChange={(e) => setDob(e.target.value)} /></div>
            <div className="flex gap-2">
              <Button onClick={fetch} className="bg-slate-900 hover:bg-slate-800 text-white gap-2 flex-1" data-testid="search-btn"><Search className="w-4 h-4" />Search</Button>
              <Button variant="outline" onClick={() => { setName(""); setDob(""); setTimeout(fetch, 0); }} data-testid="clear-btn">Clear</Button>
            </div>
          </div>
          {(name || dob) && lastReferral && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900" data-testid="last-referral-note">
              Most recent matching referral for this client was submitted on <strong>{fmtDate(lastReferral.created_at)}</strong> (status: {lastReferral.status}).
            </div>
          )}
          {(name || dob) && !loading && items.length === 0 && (
            <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600" data-testid="no-match-note">
              No referrals found for this client. You may proceed with a new referral.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-600" />Referral History</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500 py-8 text-center">Loading...</p>
          ) : items.length === 0 ? (
            <p className="text-slate-500 py-8 text-center" data-testid="no-referrals">No referrals submitted yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Date of Birth</TableHead>
                  <TableHead>Needs</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} data-testid={`referral-row-${r.id}`}>
                    <TableCell className="font-medium text-slate-900">{r.first_name} {r.last_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.date_of_birth || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[220px]">{(r.assistance_type || []).join(", ") || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDate(r.created_at)}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[r.status] || STATUS_STYLES.new}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyReferrals;
